import { Avatar, Badge, Box, Chip, Dialog, Popover, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { SconIcon } from '../../../shared/assets';
import { DeleteConfirmationDialog, List } from '../../../shared/components';
import { useUserContext } from '../../../shared/contexts';
import { businessEmployeeService, feedback } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { BusinessEmployeesRegister } from './BusinessEmployee';
import { BusinessEmployeesSconApp } from './BusinessEmployeeSconApp';
import { Button } from '../../../shared/components/MUI/button/Button';

interface IProps extends IPropsBusinessPage {
  businessAdminId: number;
}

export const BusinessEmployees: React.FC<IProps> = ({ business, refetch }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [anchorElProfilePopoverNew, setAnchorElProfilePopoverNew] = useState<null | HTMLElement>(null);
  const [openDialogNewEmployeeSconApp, setOpenDialogNewEmployeeSconApp] = useState(false);
  const [openDialogNewReceptionistSconApp, setOpenDialogNewReceptionistSconApp] = useState(false);
  const [openDialogBusinessAdmin, setOpenDialogBusinessAdmin] = useState(false);
  const [id, setId] = useState<number>();

  const { userId } = useUserContext();

  const isPopoverNewOpen = Boolean(anchorElProfilePopoverNew);

  const onClickEdit = useCallback((id: number) => {
    setOpenRegisterDialog(true);
    setId(id);
  }, []);

  const handleClose = useCallback(() => {
    setOpenRegisterDialog(false);
    setOpenDialogNewEmployeeSconApp(false);
    setOpenDialogNewReceptionistSconApp(false);
    setOpenDialogBusinessAdmin(false);
    setOpenDeleteDialog(false);
    setAnchorElProfilePopoverNew(null);
  }, []);

  // Só limpa o id depois que a animação de saída do Dialog terminar,
  // evitando flash em todos os campos do formulário durante o fechamento.
  const handleDialogExited = useCallback(() => {
    setId(undefined);
  }, []);

  const onClickDelete = useCallback((id: number) => {
    setOpenDeleteDialog(true);
    setId(id);
  }, []);

  const { mutate: remove, isLoading: isLoadingDelete } = useMutation((id: number) =>
    businessEmployeeService.remove(id), {
    onSuccess: () => {
      refetch();
      handleClose();
    },
    onError: (error: Error) => feedback(error.message, 'error'),
  });

  const onClickConfirmDelete = useCallback(() => {
    id && remove(id);
  }, [id]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      gap={3}
      padding={3}
    >
      <Box display='flex' flex={1} justifyContent='end'>
        <Button
          label='Novo'
          variant='contained'
          size='large'
          onClick={(e) => setAnchorElProfilePopoverNew(e.currentTarget)}
          data-testid='btn-new-employee'
        />

        <Popover
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          anchorEl={anchorElProfilePopoverNew}
          open={isPopoverNewOpen}
          onClose={() => setAnchorElProfilePopoverNew(null)}
        >
          <Box display='flex' flexDirection='column'>
            <Button
              label='Profissional'
              onClick={() => {
                setId(undefined);
                setOpenRegisterDialog(true);
              }}
              borderRounded={false}
              sx={{ justifyContent: 'initial', color: 'text.primary' }}
              data-testid='btn-new-employee-standard'
            />

            <Button
              label='Profissional SconApp'
              onClick={() => setOpenDialogNewEmployeeSconApp(true)}
              borderRounded={false}
              sx={{ justifyContent: 'initial', color: 'text.primary' }}
            />

            <Button
              label='Recepcionista SconApp'
              onClick={() => setOpenDialogNewReceptionistSconApp(true)}
              borderRounded={false}
              sx={{ justifyContent: 'initial', color: 'text.primary' }}
            />

            <Button
              label='Proprietário como profissional'
              onClick={() => setOpenDialogBusinessAdmin(true)}
              borderRounded={false}
              sx={{ justifyContent: 'initial', color: 'text.primary' }}
              disabled={business.employees?.some(item => item.employee?.userId == userId)}
            />
          </Box>
        </Popover>
      </Box>

      <List
        list={
          business.employees!.map((item) => ({
            id: item.id,
            description: item.name
              + (item.employee?.userId == userId ? ' (Você)' : ''),
            endAdornment: (!!item.employeeId || (!isMobile && (item.isReceptionist || !item.isActive))) && (
              <Box display='flex' gap={0.5} alignItems='center'>
                {!isMobile && (
                  <>
                    {item.isReceptionist && <Chip label='Recepcionista' size='small' color='info' variant='outlined' />}
                    {!item.isActive && <Chip label='Inativo' size='small' color='default' variant='outlined' />}
                  </>
                )}
                {!!item.employeeId && (
                  <Tooltip title='É um usuário SconApp'>
                    <img
                      src={SconIcon}
                      alt='logo'
                      height={30}
                      width={30}
                    />
                  </Tooltip>
                )}
              </Box>
            ),
            startAdornment: (
              <Badge badgeContent=''
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: item.color
                  }
                }}>
                <Avatar src={item.profilePicture} slotProps={{ img: { loading: 'lazy' } }}>{item.name.substring(0, 1)}</Avatar>
              </Badge>
            )
          }))}
        onClickEdit={onClickEdit}
        onClickDelete={onClickDelete}
      />

      <Dialog
        open={openRegisterDialog}
        onClose={handleClose}
        TransitionProps={{ onExited: handleDialogExited }}
      >
        <BusinessEmployeesRegister
          isCreateBusinessAdmin={false}
          businessEmployeeId={id}
          business={business}
          refetch={refetch}
          onCloseEdit={handleClose}
        />
      </Dialog>

      <Dialog
        open={openDialogNewEmployeeSconApp}
        onClose={() => setOpenDialogNewEmployeeSconApp(false)}
      >
        <BusinessEmployeesSconApp
          businessId={business.id}
          onCloseEdit={handleClose}
        />
      </Dialog>

      <Dialog
        open={openDialogNewReceptionistSconApp}
        onClose={() => setOpenDialogNewReceptionistSconApp(false)}
      >
        <BusinessEmployeesSconApp
          businessId={business.id}
          onCloseEdit={handleClose}
          isReceptionist
        />
      </Dialog>

      <Dialog
        open={openDialogBusinessAdmin}
        onClose={handleClose}
      >
        <BusinessEmployeesRegister
          isCreateBusinessAdmin={true}
          business={business}
          refetch={refetch}
          onCloseEdit={handleClose}
        />
      </Dialog>

      <DeleteConfirmationDialog
        onClose={handleClose}
        onConfirm={onClickConfirmDelete}
        open={openDeleteDialog}
        isLoading={isLoadingDelete}
        label='Tem certeza que deseja deletar este profissional?'
      />
    </Box>
  );
};